import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { RouterProvider as AriaRouterProvider } from "react-aria-components";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { router } from "./router";
import { queryClient } from "./lib/queryClient";
import { setSessionExpiredHandler } from "./lib/apiClient";
import { spaHref } from "./lib/spaHref";
import i18n from "./i18n";
import { initTheme } from "./stores/themeStore";
import "./styles.css";

initTheme();

// Globální 401 handler — expirovaná session / cookie → tvrdý redirect na login
// s hláškou o vypršení a návratovou cestou. Guard `redirecting` zabrání
// vícenásobnému assignu, když 401 spadne z několika souběžných dotazů naráz.
let redirecting = false;
setSessionExpiredHandler((currentPath) => {
  if (redirecting || currentPath.startsWith("/app/login")) return;
  redirecting = true;
  const next = encodeURIComponent(currentPath);
  window.location.assign(`/app/login?expired&next=${next}`);
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        {/* react-aria RouterProvider → UUI odkazy (sidebar nav) dělají SPA
            navigaci přes TanStack Router místo full reloadu. `useHref` doplní
            basepath `/app` i do renderovaného atributu href, aby odkaz fungoval
            i mimo klik (nový panel, kopírování odkazu) — viz lib/spaHref. */}
        <AriaRouterProvider
          navigate={(to) => void router.navigate({ to: to as string })}
          useHref={(to) => spaHref(to as string, (path) => router.buildLocation({ to: path }).href)}
        >
          <RouterProvider router={router} />
        </AriaRouterProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
